/**
 * Area unit conversion — one factor for the whole app.
 *
 * The backend converts with **1 sq. metre = 10.764 sq. feet**, and plot areas
 * are stored in sq ft derived from that. The frontend has to use the SAME
 * factor or a converted value won't round-trip: a plot stored as exactly
 * 930 m² becomes 10,010.52 sq ft, which the old frontend constant (0.092903)
 * turned back into 930.007 — displayed as "930.01 sq mtr" on the Layout tab
 * while the DWG tab, reading m² straight from the drawing, showed "930.0".
 * Same plot, two numbers, purely from the conversion.
 *
 * Kept in its own module rather than the `utils` barrel so the public share
 * page can use it without pulling in the toaster and auth helpers.
 */

/** The backend's conversion factor. Do not "improve" this to the exact
 *  0.09290304 / 10.763910417 — matching the backend matters more than
 *  matching the SI definition, or the two disagree again. */
export const SQFT_PER_SQM = 10.764

/** Square feet → square metres. */
export const sqftToSqm = (sqft: number): number => sqft / SQFT_PER_SQM

/** Square metres → square feet. */
export const sqmToSqft = (sqm: number): number => sqm * SQFT_PER_SQM

/**
 * Area for display: thousands separators and **up to** 3 decimals — trailing
 * zeros are dropped, so an exact 930 reads "930", not "930.000", while a
 * genuinely fractional area keeps its precision.
 */
export const formatArea = (value: number): string =>
  value.toLocaleString(undefined, { maximumFractionDigits: 3 })

/** Convenience for the plot tooltips: sq ft in, formatted sq mtr out. */
export const formatSqmFromSqft = (sqft: number): string =>
  formatArea(sqftToSqm(sqft))
